import { PersonAdd } from '@mui/icons-material';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { DialogActions } from '@mui/material';
import { getCompanyUsers } from 'lib/api';
import { generateLead } from 'lib/api/publicMarket';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Title from 'lib/designSystem/Title';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import { User } from 'lib/types/models';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

const getMessage = (productTypeId: string | undefined) => {
  if (productTypeId === 'sgr') {
    return `Se creará una línea de crédito para el producto SGR con un cupo total de $800.000. Esta línea incluirá el subproducto Seriedad de Oferta con el mismo monto. La solicitud quedará asociada y asignada automáticamente al ejecutivo seleccionado.`;
  }
  return '';
};

const GenerateLead = ({
  open,
  onClose,
  productTypeId,
  businessIdentifier,
  refetchCreditLines,
}: {
  open: boolean;
  onClose: () => void;
  productTypeId: string | undefined;
  businessIdentifier: string | null;
  refetchCreditLines: () => Promise<void>;
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedExecutive, setSelectedExecutive] = useState<
    number | undefined
  >(undefined);

  const isValid = useMemo(() => {
    return Boolean(businessIdentifier && productTypeId && selectedExecutive);
  }, [businessIdentifier, productTypeId, selectedExecutive]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getCompanyUsers(dispatch);
      setUsers(response);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLead = async () => {
    try {
      setLoading(true);
      if (!businessIdentifier || !productTypeId || !selectedExecutive) {
        showAlert(dispatch, 'error', 'Debes completar todos los campos');
        return;
      }
      await generateLead(dispatch, {
        businessIdentifier,
        productTypeId,
        executiveId: selectedExecutive,
        enrolledPath: 'public-market',
      });
      await refetchCreditLines();
      showAlert(dispatch, 'success', 'Lead generado correctamente');
      onClose();
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Title
          title="Generar lead"
          icon={<PersonAdd color="primary" />}
          color="primary"
        />
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="dense">
          <InputLabel id="executive-label">Asignar ejecutivo</InputLabel>
          <Select
            labelId="executive-label"
            id="executive-select"
            value={selectedExecutive}
            label="Asignar ejecutivo"
            onChange={(e) => setSelectedExecutive(Number(e.target.value))}
          >
            {users.map((user: User, index: number) => (
              <MenuItem key={index} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <AlertBanner
          severity="info"
          title="Información importante"
          message={getMessage(productTypeId)}
        />
      </DialogContent>
      <DialogActions>
        <CustomButton
          color="primary"
          onClick={handleGenerateLead}
          loading={loading}
          disabled={!isValid}
        >
          Generar lead
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateLead;
